import api from './api'
import { API_ENDPOINTS } from '../config/api'

// jsGames endpoints use the GameResponseHandler envelope:
// { success, code, message, payload }
const jsGamesAdminService = {
  categories: async () => {
    const response = await api.get(API_ENDPOINTS.JSGAMES.CATEGORIES)
    return response.data
  },
  getPriority: async (category) => {
    const response = await api.get(`${API_ENDPOINTS.JSGAMES.CATEGORY_PRIORITY}/${category}/priority`)
    return response.data
  },
  // Full replace — send the complete ordered id list
  setPriority: async (category, gameIds) => {
    const response = await api.post(`${API_ENDPOINTS.JSGAMES.CATEGORY_PRIORITY}/${category}/priority`, {
      game_ids: gameIds,
    })
    return response.data
  },
  listGames: async (params) => {
    const response = await api.get(API_ENDPOINTS.JSGAMES.ADMIN_GAMES, { params })
    return response.data
  },
  updateGame: async (id, data) => {
    const response = await api.put(`${API_ENDPOINTS.JSGAMES.ADMIN_GAMES}/${id}`, data)
    return response.data
  },
  // Upload tile art; the server stores it and answers with the updated game
  // (game_icon = its new long-cached URL)
  uploadGameIcon: async (id, file) => {
    const formData = new FormData()
    formData.append('image', file)
    const response = await api.post(`${API_ENDPOINTS.JSGAMES.ADMIN_GAMES}/${id}/icon`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },
  // Replace a game's manual category membership. categories = ['crash', 'slot', ...]
  setGameCategories: async (id, categories) => {
    const response = await api.put(`${API_ENDPOINTS.JSGAMES.ADMIN_GAMES}/${id}/categories`, { categories })
    return response.data
  },
  // Move a game into a provider group of `gameCategory`, and optionally into one
  // of that group's tabs. provider = slug or null to unassign; tab = slug, null
  // to untab it, or undefined to leave the tab alone.
  setGameSlotProvider: async (id, provider, tab, gameCategory = 'slot') => {
    const body = { provider }
    if (tab !== undefined) body.category = tab
    const response = await api.put(
      `${API_ENDPOINTS.JSGAMES.ADMIN_CATEGORY}/${gameCategory}/games/${id}/provider`, body,
    )
    return response.data
  },
  // --- Provider groups, per category ---
  providers: async (category, params) => {
    const response = await api.get(`${API_ENDPOINTS.JSGAMES.CATEGORY_PRIORITY}/${category}/providers`, { params })
    return response.data
  },
  // Ordered group incl. inactive games; provider 'ungrouped' = unassigned slots
  providerGames: async (category, provider) => {
    const response = await api.get(`${API_ENDPOINTS.JSGAMES.ADMIN_CATEGORY}/${category}/providers/${provider}/games`)
    return response.data
  },
  // Full replace — send the complete ordered id list for the group
  setProviderGames: async (category, provider, gameIds) => {
    const response = await api.post(
      `${API_ENDPOINTS.JSGAMES.ADMIN_CATEGORY}/${category}/providers/${provider}/games`,
      { game_ids: gameIds },
    )
    return response.data
  },
  createProvider: async (category, label) => {
    const response = await api.post(`${API_ENDPOINTS.JSGAMES.ADMIN_CATEGORY}/${category}/providers`, { label })
    return response.data
  },
  renameProvider: async (category, provider, label) => {
    const response = await api.put(`${API_ENDPOINTS.JSGAMES.ADMIN_CATEGORY}/${category}/providers/${provider}`, { label })
    return response.data
  },
  deleteProvider: async (category, provider) => {
    const response = await api.delete(`${API_ENDPOINTS.JSGAMES.ADMIN_CATEGORY}/${category}/providers/${provider}`)
    return response.data
  },
  // Provider order is also slot queue order in the app
  reorderProviders: async (category, providers) => {
    const response = await api.put(`${API_ENDPOINTS.JSGAMES.ADMIN_CATEGORY}/${category}/providers/order`, { providers })
    return response.data
  },
  // --- Tabs inside a provider group ---
  // Jili → Slot Game / Table Game / Fish Game / Crash Game. Tab order orders the
  // tab strip on the player page.
  slotProviderCategories: async (provider) => {
    const response = await api.get(`${API_ENDPOINTS.JSGAMES.ADMIN_SLOT_PROVIDERS}/${provider}/categories`)
    return response.data
  },
  // One tab's ordered games incl. inactive; slug 'uncategorized' = the group's
  // games no tab claims
  providerTabGames: async (category, provider, slug) => {
    const response = await api.get(
      `${API_ENDPOINTS.JSGAMES.ADMIN_CATEGORY}/${category}/providers/${provider}/categories/${slug}/games`)
    return response.data
  },
  // Full replace — send the complete ordered id list for the tab
  setProviderTabGames: async (category, provider, slug, gameIds) => {
    const response = await api.post(
      `${API_ENDPOINTS.JSGAMES.ADMIN_CATEGORY}/${category}/providers/${provider}/categories/${slug}/games`,
      { game_ids: gameIds },
    )
    return response.data
  },
  createProviderTab: async (category, provider, label) => {
    const response = await api.post(
      `${API_ENDPOINTS.JSGAMES.ADMIN_CATEGORY}/${category}/providers/${provider}/categories`, { label })
    return response.data
  },
  renameProviderTab: async (category, provider, slug, label) => {
    const response = await api.put(
      `${API_ENDPOINTS.JSGAMES.ADMIN_CATEGORY}/${category}/providers/${provider}/categories/${slug}`, { label })
    return response.data
  },
  deleteProviderTab: async (category, provider, slug) => {
    const response = await api.delete(
      `${API_ENDPOINTS.JSGAMES.ADMIN_CATEGORY}/${category}/providers/${provider}/categories/${slug}`)
    return response.data
  },
  // Tab order is also app order inside the provider
  reorderProviderTabs: async (category, provider, tabs) => {
    const response = await api.put(
      `${API_ENDPOINTS.JSGAMES.ADMIN_CATEGORY}/${category}/providers/${provider}/categories/order`,
      { categories: tabs },
    )
    return response.data
  },
  vendors: async (params) => {
    const response = await api.get(API_ENDPOINTS.JSGAMES.ADMIN_VENDORS, { params })
    return response.data
  },
}

export default jsGamesAdminService
